/**
 * TreeCorrectionsPanel.jsx — Family-manager queue for "Suggest a Fix".
 *
 * Renders inside the family tree Access tab. Only owner / admins /
 * editors can access — backed by GET /api/family-trees/:id/corrections.
 * Managers can Accept, Reject, or Apply. Applying writes the suggested
 * value straight to the canonical `family_members` document.
 */
import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, X, Zap, MessageSquare } from "lucide-react";

const STATUS_STYLE = {
  submitted:        { bg: "#FEF3C7", fg: "#7A5A2E" },
  accepted:         { bg: "#DCFCE7", fg: "#166534" },
  partially_accepted:{ bg: "#DDD6FE", fg: "#5B4A73" },
  rejected:         { bg: "#FEE2E2", fg: "#7F1D1D" },
  needs_more_info:  { bg: "#E0F2FE", fg: "#075985" },
  applied:          { bg: "#D1FAE5", fg: "#065F46" },
};

export default function TreeCorrectionsPanel({ treeId }) {
  const [items, setItems] = useState(null);
  const [statusFilter, setStatusFilter] = useState("submitted");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get(`/family-trees/${treeId}/corrections`, {
        params: { status: statusFilter },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load suggestions");
      setItems([]);
    }
  }, [treeId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const decide = async (cid, next, note = null) => {
    setBusyId(cid);
    try {
      await http.patch(`/corrections/${cid}`, { status: next, reviewer_notes: note });
      toast.success(next === "applied" ? "Applied to the profile" : `Marked ${next.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setBusyId(null); }
  };

  if (items === null) {
    return (
      <section data-testid="corrections-panel">
        <div className="overline mb-3">SUGGESTIONS TO REVIEW · loading…</div>
        <div className="text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
      </section>
    );
  }

  return (
    <section data-testid="corrections-panel">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="overline">SUGGESTIONS TO REVIEW · {items.length}</div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-md border text-xs px-2 py-1"
          style={{ borderColor: "var(--wofy-line)" }}
          data-testid="corrections-status-filter"
        >
          <option value="submitted">Submitted</option>
          <option value="accepted">Accepted (not yet applied)</option>
          <option value="applied">Applied</option>
          <option value="rejected">Rejected</option>
          <option value="needs_more_info">Needs more info</option>
          <option value="all">All</option>
        </select>
      </div>
      {items.length === 0 ? (
        <p className="text-sm opacity-70" data-testid="corrections-empty">
          {statusFilter === "submitted"
            ? "No pending suggestions — you're all caught up."
            : `No corrections in "${statusFilter.replace("_", " ")}" state.`}
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((c) => {
            const style = STATUS_STYLE[c.status] || STATUS_STYLE.submitted;
            const isSubmitted = c.status === "submitted";
            return (
              <li
                key={c.id}
                className="rounded-lg border p-3"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid={`correction-row-${c.id}`}
              >
                <div className="flex flex-wrap items-baseline gap-2 mb-2 text-sm">
                  <b className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--wofy-primary)" }}>{c.field}</b>
                  <span
                    className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full"
                    style={{ background: style.bg, color: style.fg }}
                  >
                    {c.status.replace("_", " ")}
                  </span>
                  <span className="text-xs opacity-60 ml-auto">
                    by {c.contributor_display_name || "Someone"} ·{" "}
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                  </span>
                </div>

                {c.original_value ? (
                  <div className="text-xs rounded-lg p-2 mb-1" style={{ background: "var(--wofy-surface)" }}>
                    <b>Current:</b> <span className="font-mono">{String(c.original_value).slice(0, 200)}</span>
                  </div>
                ) : null}
                <div className="text-sm rounded-lg p-2" style={{ background: "#F0FDF4", border: "1px solid #86EFAC" }}>
                  <b>Suggested:</b> <span className="whitespace-pre-wrap">{String(c.suggested_value).slice(0, 500)}</span>
                </div>

                {(c.reason || c.source_url) && (
                  <div className="text-xs mt-2 opacity-80 flex items-start gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5 mt-0.5 opacity-70" />
                    <span>
                      {c.reason}
                      {c.source_url && (
                        <> — <a href={c.source_url} target="_blank" rel="noreferrer" className="underline">source</a></>
                      )}
                    </span>
                  </div>
                )}

                {c.reviewer_notes && (
                  <div className="text-xs mt-2 opacity-70 italic">Reviewer note: {c.reviewer_notes}</div>
                )}

                {isSubmitted && (
                  <div className="flex gap-2 mt-3 justify-end">
                    <button
                      onClick={() => decide(c.id, "rejected", window.prompt("Reason (optional)") || null)}
                      disabled={busyId === c.id}
                      className="wofy-btn-ghost text-xs"
                      style={{ color: "#B23A3A" }}
                      data-testid={`correction-reject-${c.id}`}
                    >
                      <X className="w-3.5 h-3.5" /> Reject
                    </button>
                    <button
                      onClick={() => decide(c.id, "accepted")}
                      disabled={busyId === c.id}
                      className="wofy-btn-ghost text-xs"
                      data-testid={`correction-accept-${c.id}`}
                    >
                      <Check className="w-3.5 h-3.5" /> Accept
                    </button>
                    <button
                      onClick={() => {
                        if (!window.confirm(`Apply "${c.suggested_value.slice(0, 60)}${c.suggested_value.length > 60 ? "…" : ""}" to the canonical profile now?`)) return;
                        decide(c.id, "applied");
                      }}
                      disabled={busyId === c.id}
                      className="wofy-btn-primary text-xs"
                      data-testid={`correction-apply-${c.id}`}
                    >
                      {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                      Accept &amp; Apply
                    </button>
                  </div>
                )}
                {c.status === "accepted" && (
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={() => decide(c.id, "applied")}
                      disabled={busyId === c.id}
                      className="wofy-btn-primary text-xs"
                      data-testid={`correction-apply-later-${c.id}`}
                    >
                      {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                      Apply now
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
